/* eslint-disable import/first */
/* eslint no-console:0 */

// UTM Parameter Tracker
// Preserves UTM parameters through the job application flow to ensure proper attribution
// when applications are submitted, allowing ahoy to track the source of applications

interface UTMParams {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
}

const UTM_KEYS: (keyof UTMParams)[] = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];

/**
 * Extracts UTM parameters from the current URL
 */
function getUTMParams(): URLSearchParams {
  const params = new URLSearchParams(window.location.search);
  const utmParams = new URLSearchParams();

  for (const key of UTM_KEYS) {
    const value = params.get(key);
    if (value) {
      utmParams.set(key, value);
    }
  }

  return utmParams;
}

/**
 * Adds UTM parameters to a URL
 */
function addUTMParamsToUrl(url: string, utmParams: URLSearchParams): string {
  if (utmParams.toString() === '') return url;

  try {
    const urlObj = new URL(url, window.location.origin);
    utmParams.forEach((value: string, key: string) => {
      urlObj.searchParams.set(key, value);
    });
    return urlObj.toString();
  } catch (e) {
    // If URL parsing fails, fall back to string concatenation
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}${utmParams.toString()}`;
  }
}

/**
 * Updates all apply links on job posting pages
 */
function updateApplyLinks(): void {
  const utmParams = getUTMParams();
  if (utmParams.toString() === '') return;

  // Find all apply links (typically in sidebar and mobile sticky footer)
  const applyLinks = document.querySelectorAll<HTMLAnchorElement>(
    'a[href*="/jobs/"][href*="/applications/new"], ' + 'a.external-button:not([href="#"])[href*="/jobs/"]',
  );

  // biome-ignore lint/complexity/noForEach: TypeScript target requires forEach
  Array.from(applyLinks).forEach((link) => {
    // Skip preview links
    if (link.href === '#') return;

    // Update the href with UTM parameters
    link.href = addUTMParamsToUrl(link.href, utmParams);
  });
}

/**
 * Adds UTM parameters to application forms
 */
function updateApplicationForms(): void {
  const utmParams = getUTMParams();
  if (utmParams.toString() === '') return;

  const applicationForm = document.querySelector<HTMLFormElement>('#application-form');
  if (!applicationForm) return;

  // Add hidden fields for each UTM parameter
  utmParams.forEach((value: string, key: string) => {
    // Check if the field already exists
    const existingField = applicationForm.querySelector<HTMLInputElement>(`input[name="${key}"]`);
    if (existingField) {
      existingField.value = value;
    } else {
      // Create a new hidden field
      const hiddenField = document.createElement('input');
      hiddenField.type = 'hidden';
      hiddenField.name = key;
      hiddenField.value = value;
      applicationForm.appendChild(hiddenField);
    }
  });

  // Also update the form action URL to preserve UTM params
  const formAction = applicationForm.action;
  applicationForm.action = addUTMParamsToUrl(formAction, utmParams);
}

/**
 * Handles talent pipeline forms (general applications)
 */
function updateTalentPipelineForms(): void {
  const utmParams = getUTMParams();
  if (utmParams.toString() === '') return;

  const talentForm = document.querySelector<HTMLFormElement>('#new_job_seeker, #job-seeker-form');
  if (!talentForm) return;

  // Add hidden fields for each UTM parameter
  utmParams.forEach((value: string, key: string) => {
    const existingField = talentForm.querySelector<HTMLInputElement>(`input[name="${key}"]`);
    if (existingField) {
      existingField.value = value;
    } else {
      const hiddenField = document.createElement('input');
      hiddenField.type = 'hidden';
      hiddenField.name = key;
      hiddenField.value = value;
      talentForm.appendChild(hiddenField);
    }
  });

  // Update form action
  const formAction = talentForm.action;
  talentForm.action = addUTMParamsToUrl(formAction, utmParams);
}

/**
 * Checks if we're on an external (careers site) page
 */
function isExternalPage(): boolean {
  return document.body.classList.contains('external-body');
}

/**
 * Initializes UTM tracking based on current page
 */
function initializeUTMTracking(): void {
  // Only run on external pages (careers site)
  if (!isExternalPage()) return;

  const currentPath = window.location.pathname;

  if (currentPath.includes('/jobs/') && !currentPath.includes('/applications/new')) {
    // Job posting page
    updateApplyLinks();

    // Also handle dynamically loaded content (e.g., React components)
    const observer = new MutationObserver(() => {
      updateApplyLinks();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  } else if (currentPath.includes('/applications/new')) {
    // Application form page
    updateApplicationForms();
  } else if (currentPath.includes('/job_seekers/new') || currentPath.includes('/talent-pipeline')) {
    // Talent pipeline form page
    updateTalentPipelineForms();
  }
}

// Initialize on DOM content loaded
document.addEventListener('DOMContentLoaded', initializeUTMTracking);

// Handle Turbolinks navigation
document.addEventListener('turbolinks:load', initializeUTMTracking);
